// pages/WhyChooseUs.js
import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaCrown,
  FaClock,
  FaShieldAlt,
  FaUsers,
  FaChartLine,
  FaHeadset,
  FaCode,
  FaRocket
} from 'react-icons/fa';
import './WhyChooseUs.css';

const WhyChooseUs = () => {
  const navigate = useNavigate();
  const features = [
    {
      id: 1,
      icon: <FaCrown />,
      title: 'Industry Expertise',
      description: '10+ years of combined experience in web development across various industries including e-commerce, SaaS, healthcare, and finance.',
      benefits: ['Latest Technologies', 'Best Practices', 'Industry Standards']
    },
    {
      id: 2,
      icon: <FaClock />,
      title: 'Timely Delivery',
      description: 'We respect deadlines and deliver projects on time. Our agile methodology ensures continuous progress and early delivery.',
      benefits: ['On-time Delivery', 'Agile Methodology', 'Progress Tracking']
    },
    {
      id: 3,
      icon: <FaShieldAlt />,
      title: 'Quality Assurance',
      description: 'Rigorous testing process ensures bug-free, secure, and high-performance websites that meet the highest quality standards.',
      benefits: ['Bug-free Code', 'Security Testing', 'Performance Optimization']
    },
    {
      id: 4,
      icon: <FaUsers />,
      title: 'Client-Centric Approach',
      description: 'Your success is our priority. We work as an extension of your team, ensuring your vision is perfectly translated into reality.',
      benefits: ['Dedicated Support', 'Regular Updates', 'Transparent Communication']
    },
    {
      id: 5,
      icon: <FaChartLine />,
      title: 'ROI Focused',
      description: 'We build websites that deliver measurable results—increased traffic, higher conversions, and improved business growth.',
      benefits: ['Performance Analytics', 'Conversion Optimization', 'Growth Strategies']
    },
    {
      id: 6,
      icon: <FaHeadset />,
      title: '24/7 Support',
      description: 'Round-the-clock technical support ensures your website runs smoothly at all times with immediate assistance when needed.',
      benefits: ['Quick Response', 'Emergency Support', 'Proactive Monitoring']
    }
  ];

  const differentiators = [
    {
      title: 'Custom Solutions',
      description: 'No template websites. Every project is built from scratch to match your unique requirements.',
      icon: <FaCode />
    },
    {
      title: 'Fast Performance',
      description: 'Lightning-fast websites optimized for speed and performance, ensuring better user experience and SEO rankings.',
      icon: <FaRocket />
    },
    {
      title: 'Scalable Architecture',
      description: 'Future-proof solutions that grow with your business, easily accommodating new features and increased traffic.',
      icon: <FaChartLine />
    },
    {
      title: 'Security First',
      description: 'Enterprise-grade security measures to protect your data and your customers information.',
      icon: <FaShieldAlt />
    }
  ];

  return (
    <section id="why-choose-us" className="section why-choose-us">
      <div className="animated-bg">
        <div className="bg-grid"></div>
      </div>
      
      <div className="container">
        <div className="section-header">
          <h2 className="section-title">Why Choose GForce Solutions</h2>
          <p className="section-subtitle">
            Discover what sets us apart and makes us the preferred choice for web development
          </p>
        </div>

        {/* Features Grid */}
        <div className="features-grid">
          {features.map((feature) => (
            <div key={feature.id} className="feature-item card">
              <div className="feature-icon">
                {feature.icon}
              </div>
              <h3>{feature.title}</h3>
              <p className="feature-description">{feature.description}</p>
              <div className="feature-benefits">
                {feature.benefits.map((benefit, index) => (
                  <span key={index} className="benefit-tag">{benefit}</span>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Differentiators */}
        <div className="differentiators">
          <h3 className="differentiators-title">What Makes Us Different</h3>
          <div className="differentiators-grid">
            {differentiators.map((diff, index) => (
              <div key={index} className="differentiator-item">
                <div className="differentiator-icon">
                  {diff.icon}
                </div>
                <div className="differentiator-content">
                  <h4>{diff.title}</h4>
                  <p>{diff.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Process Comparison */}
        <div className="process-comparison card">
          <div className="comparison-header">
            <h3>Our Process vs Traditional Agencies</h3>
            <p>Experience the difference in our collaborative approach</p>
          </div>
          <div className="comparison-content">
            <div className="our-process">
              <h4>Our Process</h4>
              <ul>
                <li>✓ Transparent Pricing with No Hidden Costs</li>
                <li>✓ Agile Development with Regular Updates</li>
                <li>✓ Dedicated Project Manager Assigned</li>
                <li>✓ Client Involvement at Every Stage</li>
                <li>✓ 6 Months Free Maintenance Included</li>
                <li>✓ Post-launch Support & Training</li>
              </ul>
            </div>
            <div className="vs-separator">
              <div className="vs-circle">VS</div>
            </div>
            <div className="traditional-process">
              <h4>Traditional Agencies</h4>
              <ul>
                <li>✗ Hidden Costs & Unexpected Charges</li>
                <li>✗ Limited Communication & Updates</li>
                <li>✗ No Dedicated Project Manager</li>
                <li>✗ Minimal Client Involvement</li>
                <li>✗ Extra Charges for Maintenance</li>
                <li>✗ Limited Post-launch Support</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Client Testimonials Preview */}
        <div className="testimonials-preview">
          <h3>Don't Just Take Our Word For It</h3>
          <div className="testimonial-cards">
            <div className="testimonial-preview card">
              <div className="testimonial-text">
                "GForce delivered our project 2 weeks ahead of schedule with exceptional quality. Their team is professional and responsive."
              </div>
              <div className="testimonial-author">
                <div className="author-name">Sarah Johnson</div>
                <div className="author-role">CEO, TechInnovate Inc.</div>
              </div>
            </div>
            <div className="testimonial-preview card">
              <div className="testimonial-text">
                "The best decision we made was choosing GForce. Our website performance improved by 300% and conversions doubled."
              </div>
              <div className="testimonial-author">
                <div className="author-name">Michael Chen</div>
                <div className="author-role">Marketing Director, Global Retail</div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="choose-us-cta card">
          <div className="cta-content">
            <h3>Ready to Experience the GForce Solutions?</h3>
            <p>Join hundreds of satisfied clients who have transformed their digital presence with our expertise.</p>
          </div>
          <div className="cta-buttons">
            <button
              className="btn"
              onClick={() => navigate('/contact')}
            >
              Start Your Project
            </button>
            <button
              className="btn btn-outline"
              onClick={() => navigate('/portfolio')}
            >
              View Case Studies
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUs;